import { z } from 'zod';
import type { AppliedFilterChip } from './AppliedFilters';

// Coerce a single-or-array URL param into a typed string array, dropping
// values not in the allowed set when one is provided.
export function stringList(
  raw: string | string[] | undefined,
): string[] {
  if (raw === undefined) return [];
  if (Array.isArray(raw)) return raw;
  return raw.length === 0 ? [] : [raw];
}

export function enumList<V extends string>(
  values: ReadonlyArray<V>,
): z.ZodEffects<
  z.ZodOptional<z.ZodUnion<[z.ZodString, z.ZodArray<z.ZodString>]>>,
  V[]
> {
  return z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((raw) =>
      stringList(raw).filter((s): s is V =>
        (values as ReadonlyArray<string>).includes(s),
      ),
    );
}

export type EmptyVariant = 'unfiltered' | 'narrow' | 'global' | null;

// Classifies the page state for picking the right empty-state variant.
//   unfiltered = the underlying dataset has nothing
//   narrow     = the active scope tab has nothing, but other scopes do
//   global     = filters or search applied with no matches
//   null       = render the list
export function classifyEmpty(input: {
  totalCount: number;
  visibleCount: number;
  narrowed: boolean;
  hasFilters: boolean;
}): EmptyVariant {
  const { totalCount, visibleCount, narrowed, hasFilters } = input;
  if (totalCount === 0) return 'unfiltered';
  if (visibleCount > 0) return null;
  if (!hasFilters && narrowed) return 'narrow';
  return 'global';
}

// Element type of an array filter key (so the labeller for a multi-select
// receives the element, not the whole array).
type Elem<V> = V extends ReadonlyArray<infer E> ? E : V;

export type ChipConfig<T, K extends keyof T> = {
  label: (value: Elem<T[K]>) => string;
};

export type ChipConfigs<T> = {
  [K in keyof T]?: ChipConfig<T, K>;
};

// Builds the AppliedFilters chips for the keys you supply a labeller for.
// Scalars render once when non-default; arrays render one chip per element.
// Removal commits `defaults[key]` for scalars or the array minus the value
// for arrays — the same shape every page wrote by hand.
export function buildChips<T extends Record<string, unknown>>(
  values: T,
  defaults: T,
  set: (partial: Partial<T>) => void,
  configs: ChipConfigs<T>,
): AppliedFilterChip[] {
  const out: AppliedFilterChip[] = [];
  for (const key of Object.keys(configs) as Array<keyof T & string>) {
    const cfg = configs[key];
    if (!cfg) continue;
    const value = values[key];
    if (Array.isArray(value)) {
      for (const v of value) {
        out.push({
          id: `${key}:${String(v)}`,
          label: cfg.label(v as Elem<T[typeof key]>),
          onRemove: () =>
            set({
              [key]: value.filter((x) => x !== v),
            } as unknown as Partial<T>),
        });
      }
      continue;
    }
    const def = defaults[key];
    if (value === def || value === '' || value === undefined || value === null || value === false) {
      continue;
    }
    out.push({
      id: key,
      label: cfg.label(value as Elem<T[typeof key]>),
      onRemove: () =>
        set({ [key]: def } as unknown as Partial<T>),
    });
  }
  return out;
}
